import { AnimatePresence, type Variants, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button, XStack, YStack } from '@/components/ui';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/typography';
import { electronApis } from '@/features/electron-api';
import {
  Screenshot,
  type CapturableWindow,
} from '@/features/screenshots/screenshot';
import { withUIDelay } from '@/lib/animate';
import { cn } from '@/lib/utils';

import { Dialog } from '../ui/dialog';

import type { ResolvableModalFC } from './modalsManager';

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

function useScreenshotPermission(enabled: boolean) {
  const [permission, setPermission] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled) {
      setPermission(null);
      return;
    }

    let cancelled = false;
    setPermission(null);

    Screenshot.checkPermission()
      .then((result) => {
        if (cancelled) return;
        setPermission(result);
      })
      .catch((error) => {
        console.error(
          '[screenshot-window-picker] permission check failed:',
          error
        );
        if (cancelled) return;
        setPermission(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return permission;
}

function ScreenshotWindowPickerLoadingView() {
  return (
    <YStack className="gap-3 px-6 pt-2 pb-6">
      <XStack className="items-center gap-2 text-foreground-muted">
        <Icons.loader className="size-4 animate-spin" />
        <Text size="sm">Checking screen recording permission...</Text>
      </XStack>
    </YStack>
  );
}

function ScreenshotWindowPickerPermissionView() {
  const [showRestartButton, setShowRestartButton] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  const handleAction = useCallback(async () => {
    if (showRestartButton) {
      electronApis.system.relaunchApp();
      return;
    }

    setIsRequesting(true);

    try {
      await Screenshot.requestPermission();
      setShowRestartButton(true);
    } finally {
      setIsRequesting(false);
    }
  }, [showRestartButton]);

  return (
    <YStack className="gap-4 px-6 pt-2 pb-6">
      <XStack className="gap-3 bg-surface rounded-lg p-3">
        <XStack className="bg-secondary rounded-md items-center justify-center size-9 shrink-0">
          <Icons.camera size={16} />
        </XStack>
        <YStack className="gap-0.5">
          <Text size="sm" fontWeight="medium">
            Allow Littlebird to capture your screen
          </Text>
          <Text size="xs" variant="muted">
            {showRestartButton
              ? 'Restart the app to apply changes'
              : 'Required by macOS to capture window contents'}
          </Text>
        </YStack>
      </XStack>

      <Button size="sm" onClick={handleAction} isLoading={isRequesting}>
        {showRestartButton ? 'Restart App' : 'Grant Access'}
      </Button>
    </YStack>
  );
}

function useWindowList(refreshToken: number) {
  const [windows, setWindows] = useState<CapturableWindow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setWindows(null);
    Screenshot.listWindows()
      .then((windows) =>
        withUIDelay(() => {
          if (cancelled) return;
          setWindows(windows);
        })
      )
      .catch((error) => {
        if (cancelled) return;
        console.error(
          '[screenshot-window-picker] failed to load windows:',
          error
        );
        setWindows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  return windows;
}

function ScreenshotWindowPickerListView({
  onCaptured,
  refreshToken,
}: {
  onCaptured: (file: File) => void;
  refreshToken: number;
}) {
  const windows = useWindowList(refreshToken);
  const [capturingWindowId, setCapturingWindowId] = useState<number | null>(
    null
  );

  const handleCapture = useCallback(
    async (window: CapturableWindow) => {
      setCapturingWindowId(window.windowId);

      try {
        const file = await Screenshot.capture(
          window.windowId,
          window.windowTitle || window.name
        );
        onCaptured(file);
      } catch (error) {
        console.error('[screenshot-window-picker] capture failed:', error);
        const msg =
          error instanceof Error
            ? error.message
            : 'Failed to capture screenshot';
        toast.error(msg);
      } finally {
        setCapturingWindowId(null);
      }
    },
    [onCaptured]
  );

  return (
    <ScrollMask
      showEndFade
      className="relative h-[200px] max-h-[200px] max-w-full overflow-x-hidden pl-4 mb-6"
    >
      <AnimatePresence>
        {windows === null && (
          <motion.div
            key="loading"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute left-4 top-0 w-full flex flex-col"
          >
            {Array.from({ length: 10 }, (_, i) => (
              <div
                key={i}
                className="flex items-center gap-2 w-full rounded-md px-2 py-1.5"
              >
                <Skeleton
                  className="h-5 rounded"
                  style={{ width: `${45 + (i % 3) * 20}%` }}
                />
              </div>
            ))}
          </motion.div>
        )}
        {windows?.length === 0 && (
          <motion.div
            key="empty"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0 text-sm text-foreground-muted py-4 text-center"
          >
            No windows available
          </motion.div>
        )}
        {windows && windows.length > 0 && (
          <motion.div
            key="list"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {windows.map((window) => (
              <button
                key={window.windowId}
                type="button"
                disabled={capturingWindowId !== null}
                onClick={() => handleCapture(window)}
                className={cn(
                  'flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-left text-sm',
                  'hover:bg-surface-active transition-colors',
                  capturingWindowId === window.windowId && 'bg-surface-active'
                )}
              >
                {window.iconURL ? (
                  <img
                    src={window.iconURL}
                    alt=""
                    className="size-5 rounded shrink-0"
                  />
                ) : (
                  <div className="size-5 rounded bg-surface-active shrink-0" />
                )}
                <span className="truncate min-w-0">
                  {window.windowTitle && window.windowTitle !== window.name
                    ? `${window.name} - ${window.windowTitle}`
                    : window.name}
                </span>
                {capturingWindowId === window.windowId && (
                  <Icons.loader className="size-4 animate-spin text-foreground-muted shrink-0" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </ScrollMask>
  );
}

export const ScreenshotWindowPickerModal: ResolvableModalFC<
  Record<string, never>,
  File | null
> = ({ open, onOpenChange, resolve }) => {
  const permission = useScreenshotPermission(open);
  const resolved = useRef(false);
  const [listRefreshToken, setListRefreshToken] = useState(0);

  useEffect(() => {
    if (open) {
      resolved.current = false;
    }
  }, [open]);

  const resolveAndClose = useCallback(
    (value: File | null) => {
      if (resolved.current) return;
      resolved.current = true;
      resolve(value);
      onOpenChange(false);
    },
    [resolve, onOpenChange]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resolveAndClose(null);
        return;
      }
      onOpenChange(nextOpen);
    },
    [resolveAndClose, onOpenChange]
  );

  useEffect(() => {
    if (open && permission === true) {
      setListRefreshToken((token) => token + 1);
    }
  }, [open, permission]);

  if (!open) return null;

  const hasPermission = permission === true;
  const isChecking = permission === null;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="max-w-md overflow-hidden p-0">
        <Dialog.Header className="px-6 pt-6">
          <Dialog.Title>
            {hasPermission
              ? 'Screenshot'
              : isChecking
                ? 'Checking Permission'
                : 'Screen Recording Required'}
          </Dialog.Title>
          {hasPermission && (
            <Dialog.Description>
              Choose a window to screenshot
            </Dialog.Description>
          )}
        </Dialog.Header>
        <div className="overflow-hidden">
          {hasPermission && (
            <ScreenshotWindowPickerListView
              onCaptured={resolveAndClose}
              refreshToken={listRefreshToken}
            />
          )}
          {!hasPermission && isChecking && (
            <ScreenshotWindowPickerLoadingView />
          )}
          {!hasPermission && !isChecking && (
            <ScreenshotWindowPickerPermissionView />
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};
